// Hard-Gate P1 (2026-09-06) — self-service Two-Factor Authentication
// enrollment/disable. Any privileged action (`require_platform_admin` /
// `require_tenant_admin`) now requires this to be enabled — see
// backend/auth.py `_mfa_gate`. Ordinary login and non-privileged work is
// never blocked by this; it only unlocks admin-only actions/pages.
import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, KeyRound, Copy, Check, X, Loader2 } from "lucide-react";

export function MfaSettingsCard() {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState("idle"); // idle | enrolling | recovery_codes
  const [secret, setSecret] = useState(null);
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  // Disable form
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disableError, setDisableError] = useState("");

  if (!user) return null;

  const startEnroll = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/start");
      setSecret(data.manual_entry_secret);
      setQr(data.qr_code_data_url);
      setStep("enrolling");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start enrollment");
    } finally {
      setBusy(false);
    }
  };

  const verifyEnroll = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/verify", { code: code.trim() });
      setRecoveryCodes(data.recovery_codes);
      setStep("recovery_codes");
      setCode("");
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Incorrect code");
    } finally {
      setBusy(false);
    }
  };

  const finishEnroll = () => {
    setStep("idle");
    setRecoveryCodes(null);
    setSecret(null);
    setQr(null);
  };

  const disableMfa = async (e) => {
    e.preventDefault();
    setDisableError("");
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: disablePassword, code: disableCode.trim() });
      toast.success("Two-factor authentication disabled");
      setShowDisable(false);
      setDisablePassword("");
      setDisableCode("");
      await refresh();
    } catch (e) {
      setDisableError(e?.response?.data?.detail || "Could not disable");
    } finally {
      setBusy(false);
    }
  };

  const copyRecovery = async () => {
    try {
      await navigator.clipboard.writeText((recoveryCodes || []).join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  // ── Recovery codes shown once, right after activation ──
  if (step === "recovery_codes") {
    return (
      <div className="fop-surface-2 rounded-lg p-5" data-testid="mfa-recovery-codes-card">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-3 inline-flex items-center gap-1.5">
          <ShieldCheck size={12} /> Two-factor authentication enabled
        </div>
        <p className="text-sm text-[var(--fop-text-2)] mb-3">
          Save these 10 recovery codes now — they will never be shown again.
          Each one works exactly once if you lose your authenticator device.
        </p>
        <div className="grid grid-cols-2 gap-1.5 font-mono text-xs bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded p-3 mb-3" data-testid="mfa-recovery-codes-list">
          {(recoveryCodes || []).map((c) => <div key={c}>{c}</div>)}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyRecovery} className="btn btn-ghost text-xs" data-testid="mfa-copy-recovery">
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy all"}
          </button>
          <button onClick={finishEnroll} className="btn btn-primary text-xs" data-testid="mfa-recovery-done">
            I've saved these — done
          </button>
        </div>
      </div>
    );
  }

  // ── Enrollment in progress: QR + secret + verify code ──
  if (step === "enrolling") {
    return (
      <div className="fop-surface-2 rounded-lg p-5" data-testid="mfa-enroll-card">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-3">
          Set up two-factor authentication
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          {qr && (
            <img src={qr} alt="MFA QR code" className="w-40 h-40 rounded border border-[var(--fop-border)] bg-white p-1 self-start" data-testid="mfa-qr-code" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-[var(--fop-text-2)] mb-2">
              Scan with an authenticator app (Google Authenticator, 1Password, Authy),
              or enter this key manually:
            </p>
            <div className="font-mono text-xs bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded px-2 py-1.5 break-all mb-3" data-testid="mfa-manual-secret">
              {secret}
            </div>
            <form onSubmit={verifyEnroll} className="space-y-2.5">
              <input
                type="text" required value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="123456" inputMode="numeric" autoFocus
                data-testid="mfa-enroll-code-input"
              />
              {error && <div className="text-[var(--fop-danger)] text-xs font-mono" data-testid="mfa-enroll-error">{error}</div>}
              <div className="flex items-center gap-2">
                <button type="submit" disabled={busy} className="btn btn-primary text-xs" data-testid="mfa-enroll-verify-submit">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                  {busy ? "Verifying…" : "Activate"}
                </button>
                <button type="button" onClick={finishEnroll} className="btn btn-ghost text-xs" data-testid="mfa-enroll-cancel">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Idle: status + enroll/disable entry points ──
  return (
    <div className="fop-surface-2 rounded-lg p-5" data-testid="mfa-settings-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1.5">
            Two-factor authentication
          </div>
          {user.mfa_enabled ? (
            <div className="inline-flex items-center gap-1.5 text-sm text-emerald-400" data-testid="mfa-status-enabled">
              <ShieldCheck size={14} /> Enabled — required for admin actions
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 text-sm text-amber-400" data-testid="mfa-status-disabled">
              <ShieldAlert size={14} /> Not enabled
              {user.mfa_enrollment_recommended && " — required to perform admin/platform actions"}
            </div>
          )}
          <p className="text-xs text-[var(--fop-text-3)] mt-1.5 max-w-md">
            {user.mfa_enabled
              ? "Your account requires an authenticator code (or recovery code) at login."
              : "Add a second factor from your authenticator app. Ordinary sign-in and daily work are never blocked by this — only admin-only actions/pages."}
          </p>
        </div>
        {!user.mfa_enabled ? (
          <button onClick={startEnroll} disabled={busy} className="btn btn-primary text-xs shrink-0" data-testid="mfa-start-enroll">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />} Set up 2FA
          </button>
        ) : (
          <button onClick={() => setShowDisable((v) => !v)} className="btn btn-ghost text-xs shrink-0" data-testid="mfa-show-disable">
            {showDisable ? <X size={12} /> : <ShieldAlert size={12} />} {showDisable ? "Cancel" : "Disable 2FA"}
          </button>
        )}
      </div>
      {showDisable && (
        <form onSubmit={disableMfa} className="mt-4 pt-4 border-t border-[var(--fop-border)] space-y-2.5 max-w-sm" data-testid="mfa-disable-form">
          <div>
            <label className="text-xs text-[var(--fop-text-2)] mb-1 block">Current password</label>
            <input type="password" required value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} data-testid="mfa-disable-password" />
          </div>
          <div>
            <label className="text-xs text-[var(--fop-text-2)] mb-1 block">Authenticator or recovery code</label>
            <input type="text" required value={disableCode} onChange={(e) => setDisableCode(e.target.value)} data-testid="mfa-disable-code" />
          </div>
          {disableError && <div className="text-[var(--fop-danger)] text-xs font-mono" data-testid="mfa-disable-error">{disableError}</div>}
          <button type="submit" disabled={busy} className="btn btn-ghost text-xs text-[var(--st-escalated)]" data-testid="mfa-disable-submit">
            Disable two-factor authentication
          </button>
        </form>
      )}
    </div>
  );
}
